/**
 * URL-sanitointi runtimessa (WP-ABP8). Peili palvelimen
 * {@code Html.sanitizeUrl}ista.
 *
 * <p><b>Miksi sama tarkistus kahdesti.</b> Palvelin sanitoi vain
 * ALKUARVON. Kun `link.href` tai `image.src` on sidottu, lopullisen arvon
 * tuottaa lauseke selaimessa ja runtime kirjoittaa sen DOMiin — silloin
 * palvelimen tarkistus ei ole ollut missään tekemisissä arvon kanssa.
 * Ilman tätä `${jokinSlot}` voisi tuottaa `javascript:`-URLin suoraan
 * ankkuriin.
 */

/** Sallitut skeemat; suhteellinen URL kelpaa aina. */
const SAFE_SCHEMES = new Set(['http', 'https', 'mailto', 'tel']);

/** Kuville lisäksi data:-URIt (CSP sallii `data:`; ks. päätös D1). */
const SAFE_IMAGE_SCHEMES = new Set([...SAFE_SCHEMES, 'data']);

/**
 * Palauta URL sellaisenaan jos se on turvallinen, muuten tyhjä merkkijono.
 *
 * <p>Kontrollimerkit poistetaan ENNEN skeeman tunnistusta: ilman sitä
 * `java\nscript:alert(1)` läpäisisi tarkistuksen ja selain silti suorittaisi
 * sen (selain jättää kontrollimerkit huomiotta skeemaa lukiessaan).
 */
export function sanitizeUrl(raw: unknown, kind: 'link' | 'image' = 'link'): string {
  if (raw === null || raw === undefined) return '';
  let url = '';
  for (const ch of String(raw)) {
    const code = ch.charCodeAt(0);
    if (code > 0x20 && code !== 0x7f) url += ch;
  }
  if (!url) return '';

  const colon = url.indexOf(':');
  if (colon < 0) return url; // suhteellinen

  // Kaksoispiste polussa (esim. "/foo:bar") ei ole skeema.
  const before = (i: number) => i >= 0 && i < colon;
  if (before(url.indexOf('/')) || before(url.indexOf('?')) || before(url.indexOf('#'))) {
    return url;
  }

  const scheme = url.slice(0, colon).toLowerCase();
  if (scheme === 'app-asset') {
    // WP-AF4 (peili palvelimen Html.expandAppAssetUrl:ista): sidotun propin
    // lopullinen arvo syntyy vasta selaimessa, joten laajennus on tehtävä
    // tässä — muuten app-asset:-viittaus katoaisi tuntemattomana skeemana.
    return expandAppAsset(url.slice(colon + 1));
  }
  const allowed = kind === 'image' ? SAFE_IMAGE_SCHEMES : SAFE_SCHEMES;
  return allowed.has(scheme) ? url : '';
}

const ASSET_PATH = /^[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*$/;

/** Asset-polun säännöt — samat kuin palvelimen `Html.isValidAssetPath`issa. */
function isValidAssetPath(path: string): boolean {
  if (!path || !ASSET_PATH.test(path)) return false;
  return path.split('/').every((seg) => seg !== '.' && seg !== '..');
}

/**
 * `app-asset:<polku>` → `/apps/<appId>/assets/<polku>`. Appin id tulee
 * palvelimen siementämästä `window.fasthub.appId`sta (AppRenderer emittoi sen
 * kaikilla renderöintipoluilla). Ilman id:tä tai kelvottomalla polulla
 * palautuu tyhjä — komponentin virhetila (fallback) näkyy, mitään ei haeta.
 * Laajennettu polku on absoluuttinen, joten srcdoc-iframen peritty base-URL
 * ei sotke viittausta.
 */
function expandAppAsset(path: string): string {
  if (!isValidAssetPath(path)) return '';
  const appId = (globalThis as { fasthub?: { appId?: unknown } }).fasthub?.appId;
  if (typeof appId !== 'string' || !appId) return '';
  return `/apps/${encodeURIComponent(appId)}/assets/${path}`;
}

/**
 * Onko kuvan osoite sellainen jonka julkaistun Appin CSP estää?
 * (`img-src 'self' data:` — päätös `D1` = A, CSP pysyy ennallaan.)
 *
 * <p>Käytetään editorin varoituksessa: absoluuttinen http(s)-URL toiseen
 * originiin ei renderöidy julkaistussa Appissa, ja se on parempi kertoa
 * rakennushetkellä kuin antaa kuvan hiljaa jäädä tyhjäksi.
 */
export function isBlockedByImageCsp(raw: unknown, origin?: string): boolean {
  const url = sanitizeUrl(raw, 'image');
  if (!url) return false;
  if (!/^https?:/i.test(url)) return false; // suhteellinen tai data: → sallittu
  try {
    const here = origin ?? (typeof window === 'undefined' ? '' : window.location.origin);
    return !!here && new URL(url).origin !== here;
  } catch {
    return false;
  }
}
